import i18n from "i18next";
import { faFileInvoiceDollar } from "@fortawesome/pro-regular-svg-icons";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import type { AsyncButtonProps } from "src/components/common/buttons/AsyncButton.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { CustomerOrdersPage } from "src/components/views/erp/sales/customerOrder/CustomerOrdersPage.tsx";
import { resolveValueOrProvider, ValueOrProvider } from "src/utils/valueOrProvider.ts";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { useCallback } from "react";

export interface OpenCustomerOrderButtonProps extends Omit<AsyncButtonProps, "onClick"> {
	customerOrderId: ValueOrProvider<number | Promise<number>> | null | undefined;
}

export function useOpenCustomerOrder() {
	const { openDialog } = useGenericDialog();
	const { tenantConfig } = useTenantCustomizations();
	const hasPermission = useHasPermission();
	const openCustomerOrder = useCallback(
		(customerOrderId: number) => {
			openDialog({
				title: i18n.t("customer_order"),
				agentContext: {
					key: defineAgentUiContextKey("erp.sales.customerOrder.openCustomerOrderButton.dialog"),
					data: { customerOrderId },
					pathSegments: [`${i18n.t("customer_order")} ${customerOrderId}`],
				},
				content: <CustomerOrdersPage onlyCustomerOrderToShowId={customerOrderId} />,
			});
		},
		[openDialog],
	);

	return tenantConfig.erp.salesEnabled && hasPermission(Permissions.Sales.CustomerOrders.READ) ?
			openCustomerOrder
		:	undefined;
}

export const OpenCustomerOrderButton = ({
	customerOrderId: customerOrderIdProp,
	variant = "menu",
	label = i18n.t("open_customer_order"),
}: OpenCustomerOrderButtonProps) => {
	const openCustomerOrder = useOpenCustomerOrder();

	return (
		openCustomerOrder != null && (
			<AsyncButton
				label={label}
				icon={faFileInvoiceDollar}
				variant={variant}
				disabled={customerOrderIdProp == null}
				onClick={async () => {
					if (customerOrderIdProp == null) return;
					const customerOrderId = await resolveValueOrProvider(customerOrderIdProp);
					openCustomerOrder(customerOrderId);
				}}
			/>
		)
	);
};
